// pages/dashboard/components/Header.tsx
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../../../services/api';
import LogoutButton from '../../../components/LogoutButton';
import BrandNotificationBell from '../../../components/BrandNotificationBell';
import CreditsWidget from '../../../components/CreditsWidget';

interface HeaderProps {
  darkMode: boolean;
  setDarkMode: (darkMode: boolean) => void;
  onTooltipShow: (title: string, content: string) => void;
}

export default function Header({ darkMode }: HeaderProps) {
  const navigate = useNavigate();
  const [userName, setUserName] = useState<string>('');
  const [brandName, setBrandName] = useState<string>(() => localStorage.getItem('brand_name') || '');
  const [logoUrl, setLogoUrl] = useState<string | null>(() => {
    const storedLogo = localStorage.getItem("logo_url");
    return storedLogo ? storedLogo.replace(/"/g, '') || null : null;
  });

  useEffect(() => {
    async function fetchUser() {
      const userId = localStorage.getItem('user_id');
      if (!userId) {
        setUserName(localStorage.getItem('full_name') || localStorage.getItem('username') || 'User');
        return;
      }
      try {
        const [profile, bizProfile] = await Promise.allSettled([
          apiService.getContactIdentity(userId),
          apiService.getWebsiteProfile(userId),
        ]);
        if (profile.status === 'fulfilled') {
          setUserName(
            profile.value?.data?.full_name ||
            localStorage.getItem('full_name') ||
            localStorage.getItem('username') ||
            'User'
          );
        }
        if (bizProfile.status === 'fulfilled') {
          const bData = bizProfile.value?.data?.business_profile || bizProfile.value?.business_profile || bizProfile.value;
          if (bData?.business_name) {
            setBrandName(bData.business_name);
            localStorage.setItem('brand_name', bData.business_name);
          }
        }
      } catch {
        const raw = localStorage.getItem("logo_url");
        setLogoUrl(raw ? raw.replace(/"/g, '') || null : null);
        setUserName(localStorage.getItem('full_name') || localStorage.getItem('username') || 'User');
      }
    }
    fetchUser();

    const handleProfileUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail && customEvent.detail.fullName) {
        setUserName(customEvent.detail.fullName);
      }
    };
    window.addEventListener('contact_identity_updated', handleProfileUpdate);
    return () => window.removeEventListener('contact_identity_updated', handleProfileUpdate);
  }, []);

  return (
    <header className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 h-[88px] flex items-center`}>
      <div className="flex items-center justify-between w-full">
        {/* Left Section - Page Title */}
        <div className="flex items-center space-x-6">
          {/* {logoUrl ? (
            <img src={logoUrl} alt={`${brandName || 'Brand'} Logo`} className="h-14 w-auto max-w-[56px] object-contain" />
          ) : (
            <img src={logo} alt="Marketing 4Sight Logo" className="h-14 w-auto" />
          )} */}
          <div>
            {/* {brandName && (
              <p className={`text-xs font-semibold truncate max-w-[200px] ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {brandName}
              </p>
            )} */}
            <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              SEO Manager
            </h2>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-3">
          {/* Export Report (disabled) */}
          <div className="hidden md:flex">
            <button
              disabled
              className="px-3 py-2 bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 rounded-lg text-sm font-medium cursor-not-allowed opacity-60 whitespace-nowrap"
            >
              <i className="ri-download-line mr-2" />
              Export Report
            </button>
          </div>

          {/* Home nav link */}
          <Link
            to="/marketing-dashboard"
            className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-all text-sm font-semibold border ${darkMode
              ? 'text-gray-400 border-gray-700 hover:text-white hover:bg-gray-700'
              : 'text-gray-500 border-gray-100 hover:text-[#5C4FCF] hover:bg-[#5C4FCF]/5'
            }`}
          >
            <i className="ri-home-4-line text-lg" />
            <span className="hidden lg:inline">Home</span>
          </Link>

          {/* Bell → Credits Pill → Buy Credits: grouped as one unit */}
          <div className="flex items-center gap-2">
            <BrandNotificationBell />
            <CreditsWidget />
          </div>

          {/* Profile */}
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full hover:bg-gray-100 transition-colors cursor-pointer group"
            title="Profile"
          >
            <div
              className={`w-8 h-8 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 ${logoUrl
                ? 'bg-white border border-gray-200'
                : 'bg-teal-100'
                }`}
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Business Logo"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-teal-600 font-semibold text-sm">
                  {userName?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              )}
            </div>
            <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 whitespace-nowrap" title="Profile">
              Profile
            </span>
            <i className="ri-arrow-right-s-line text-gray-400 text-base"></i>
          </button>

          <LogoutButton
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-all"
          >
            <i className="ri-logout-box-r-line text-[18px] text-slate-600" />
            <span className="hidden lg:inline text-sm font-semibold text-slate-700">Logout</span>
          </LogoutButton>
        </div>
      </div>
    </header>
  );
}

